import React, {
	useCallback,
	useContext,
	useState,
} from "react";
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@mui/material";
import {
	darken,
	lighten,
} from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
	format,
	subDays,
} from "date-fns";
import { DataContext } from "../../App";
import useFetchData from "../Shared/FetchData/FetchData";

const weekdayFormatter = new Intl.DateTimeFormat("en-US", {
	weekday: "short",
	timeZone: "UTC",
});

const volumeDateFormatter = new Intl.DateTimeFormat("en-US", {
	month: "short",
	day: "numeric",
	year: "numeric",
	timeZone: "UTC",
});

const sellerVolumeTitle = "Orders Created Per Seller";

const emptyVolume = {
	dates: [],
	totals: [],
	sellers: [],
};

export function formatVolumeDelta(delta) {
	if (delta === null || delta === undefined) {
		return "-";
	}
	return `${Math.round(delta * 100)}%`;
}

export function volumeRowTone(delta) {
	if (delta === null || delta === undefined || Number.isNaN(Number(delta))) {
		return "";
	}
	const percent = Math.round(Number(delta) * 100);
	if (percent > 0) {
		return "green";
	}
	if (percent >= -20) {
		return "amber";
	}
	return "red";
}

function volumeRowColour(theme, tone) {
	const tint = theme.palette.mode === "dark"
		? (colour) => darken(colour, 0.7)
		: (colour) => lighten(colour, 0.85);
	if (tone === "green") {
		return tint(theme.palette.success.main);
	}
	if (tone === "amber") {
		return tint(theme.palette.warning.main);
	}
	return tint(theme.palette.error.main);
}

function volumeRowSx(delta) {
	const tone = volumeRowTone(delta);
	if (!tone) {
		return undefined;
	}
	const backgroundColor = (theme) => volumeRowColour(theme, tone);
	return {
		backgroundColor,
		"&&:nth-of-type(even)": { backgroundColor },
		"& > .MuiTableCell-root": { backgroundColor },
	};
}

export function formatVolumeWeekday(date) {
	return weekdayFormatter.format(new Date(`${date}T00:00:00Z`));
}

export function formatVolumeDate(date) {
	return volumeDateFormatter.format(new Date(`${date}T00:00:00Z`));
}

function sellerValue(row, key, dates) {
	if (key === "seller") {
		return row.seller;
	}
	if (key === "avg_l10d" || key === "avg_l4d") {
		return row[key];
	}
	if (key === "delta") {
		return row.delta;
	}
	return row.orders[dates.indexOf(key)] || 0;
}

function compareValues(left, right) {
	if (typeof left === "string" || typeof right === "string") {
		return String(left).localeCompare(String(right));
	}
	if (left == null && right == null) {
		return 0;
	}
	if (left == null) {
		return 1;
	}
	if (right == null) {
		return -1;
	}
	return left - right;
}

export function sortSellerRows(rows, dates, sort) {
	if (!sort.key) {
		return rows;
	}
	const direction = sort.direction === "asc" ? 1 : -1;
	return [...rows].sort((left, right) => {
		const leftValue = sellerValue(left, sort.key, dates);
		const rightValue = sellerValue(right, sort.key, dates);
		if (leftValue == null || rightValue == null) {
			return compareValues(leftValue, rightValue);
		}
		return compareValues(leftValue, rightValue) * direction;
	});
}

function volumeFromResults(results) {
	if (!results || Array.isArray(results) || !Array.isArray(results.sellers)) {
		return emptyVolume;
	}
	return results;
}

export default function SellerVolumeTable() {
	const context = useContext(DataContext);
	const [open, setOpen] = useState(false);
	const [endDate, setEndDate] = useState(() => new Date());
	const [refreshToken, setRefreshToken] = useState(0);
	const [sellerFilter, setSellerFilter] = useState("");
	const [sort, setSort] = useState({ key: "", direction: "desc" });
	const loadVolume = useCallback(() => {
		const toDate = format(endDate, "yyyy-MM-dd");
		const fromDate = format(subDays(endDate, 9), "yyyy-MM-dd");
		return context.dataProvider.getSellerVolume(fromDate, toDate);
	}, [context.dataProvider, endDate, refreshToken]);
	const volumeRequest = useFetchData(open ? loadVolume : null);
	const volume = volumeFromResults(volumeRequest.results);
	const filteredSellers = volume.sellers.filter((row) => (
		row.seller.toLowerCase().includes(sellerFilter.trim().toLowerCase())
	));
	const sellers = sortSellerRows(filteredSellers, volume.dates, sort);
	const columns = [
		{ key: "seller", label: "Seller" },
		...volume.dates.map((date) => ({ key: date, label: date })),
		{ key: "avg_l10d", label: "Avg L10D" },
		{ key: "avg_l4d", label: "Avg L4D" },
		{ key: "delta", label: "% Delta" },
	];

	const toggleSort = (key) => {
		setSort((current) => {
			if (current.key !== key) {
				return { key, direction: key === "seller" ? "asc" : "desc" };
			}
			return { key, direction: current.direction === "asc" ? "desc" : "asc" };
		});
	};

	return (
		<Paper sx={{ p: 2, width: "100%", overflowX: "auto" }} elevation={2}>
			<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: open ? 2 : 0 }}>
				<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
					<IconButton
						aria-expanded={open}
						aria-label={`${open ? "Collapse" : "Expand"} ${sellerVolumeTitle}`}
						onClick={() => setOpen((value) => !value)}
						size="small"
					>
						{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
					<Typography component="h2" variant="h6">{sellerVolumeTitle}</Typography>
				</Box>
				{open ? (
					<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<DesktopDatePicker
								label="End date"
								inputFormat="dd/MM/yyyy"
								value={endDate}
								disableFuture
								onChange={(value) => {
									if (value instanceof Date && !Number.isNaN(value.getTime())) {
										setEndDate(value);
									}
								}}
								renderInput={(params) => (
									<TextField {...params} size="small" sx={{ maxWidth: 160 }} />
								)}
							/>
						</LocalizationProvider>
						<Button
							aria-label={`Refresh ${sellerVolumeTitle}`}
							onClick={() => setRefreshToken((value) => value + 1)}
							disabled={volumeRequest.isLoading}
							size="small"
							variant="outlined"
							startIcon={<RefreshIcon />}
						>
							Refresh
						</Button>
					</Box>
				) : null}
			</Box>
			<Collapse in={open} unmountOnExit>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell key={column.key} sortDirection={sort.key === column.key ? sort.direction : false}>
									<TableSortLabel
										active={sort.key === column.key}
										direction={sort.key === column.key ? sort.direction : "asc"}
										onClick={() => toggleSort(column.key)}
									>
										{column.key === "seller" || column.key === "avg_l10d" || column.key === "avg_l4d" || column.key === "delta" ? column.label : (
											<Box>
												<Box>{formatVolumeWeekday(column.label)}</Box>
												<Box>{formatVolumeDate(column.label)}</Box>
											</Box>
										)}
									</TableSortLabel>
									{column.key === "seller" ? (
										<TextField
											size="small"
											placeholder="Filter"
											value={sellerFilter}
											onClick={(event) => event.stopPropagation()}
											onChange={(event) => setSellerFilter(event.target.value)}
											inputProps={{ "aria-label": "Filter sellers" }}
											sx={{ display: "block", mt: 1, minWidth: 90 }}
										/>
									) : null}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{volumeRequest.isLoading ? (
							<TableRow>
								<TableCell colSpan={Math.max(columns.length, 1)} align="center">
									<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
										<CircularProgress aria-label={`Loading ${sellerVolumeTitle}`} />
									</Box>
								</TableCell>
							</TableRow>
						) : volumeRequest.status === false ? (
							<TableRow>
								<TableCell colSpan={Math.max(columns.length, 1)} align="center">
									<Typography role="alert" color="error" sx={{ py: 3 }}>
										Unable to load this table. Please try again.
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							<>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }} />
									{volume.dates.map((date, index) => (
										<TableCell key={date} sx={{ fontWeight: 700 }}>{volume.totals[index]}</TableCell>
									))}
									<TableCell /><TableCell /><TableCell />
								</TableRow>
								{sellers.map((row) => (
									<TableRow key={row.seller} data-tone={volumeRowTone(row.delta) || undefined} sx={volumeRowSx(row.delta)}>
										<TableCell>{row.seller}</TableCell>
										{row.orders.map((orders, index) => (
											<TableCell key={volume.dates[index]}>{orders}</TableCell>
										))}
										<TableCell>{row.avg_l10d}</TableCell>
										<TableCell>{row.avg_l4d}</TableCell>
										<TableCell>{formatVolumeDelta(row.delta)}</TableCell>
									</TableRow>
								))}
							</>
						)}
					</TableBody>
				</Table>
			</Collapse>
		</Paper>
	);
}
